import { parseISO } from "date-fns";
import format from "date-fns/format";
import moment from "moment";
import { parse, isValid } from "date-fns";

// for formatting dateCreated, dateUpdated, dateUploaded

export const getDateToday = () => format(Date.now(), "MMM dd, yyyy");

// Date formatted (USED IN HESVPARTS UPDATE PERIOD)
export const parseDate = (dateString: string) => {
  const formats = [
    "yyyy-MM-dd",
    "MM/dd/yyyy",
    "yyyy-MM-dd HH:mm:ssXXX",
    "dd-MM-yyyy",
  ];
  for (let format of formats) {
    const parsedDate = parse(dateString, format, new Date());
    if (isValid(parsedDate)) {
      return parsedDate;
    }
  }
  return null;
};

export const getFullDate = (date: string | Date): string => {
  const dateObj = new Date(date);

  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "long",
  };

  const formattedDate = new Intl.DateTimeFormat("en-US", options).format(
    dateObj
  );

  const hours = dateObj.getHours();
  const minutes = dateObj.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  const formattedTime = `${hours % 12 || 12}:${String(minutes).padStart(
    2,
    "0"
  )} ${ampm}`;

  // Combine everything
  return `${formattedDate}_${formattedTime}`;
};

export const toTitleCase = (str: string) => {
  if (str === "" || str === undefined || str === null) return;
  const result = str.replace(/([A-Z])/g, " $1");
  const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
  return finalResult.trim();
};

export const formatDate = (date: string | Date) => {
  if (!date) return;
  return format(parseISO(date.toString()), "MMMM dd, yyy");
};

export const formatCurrency = (price: number | string, currency: string = "") =>
  Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
    .format(typeof price === "string" ? Number(price) : price)
    .replace("₱", currency + " ");

export const dateDifference = (date1: string | Date, date2: string | Date) =>
  moment(date1).diff(formatDate(date2), "days");

const appendFilesFromData = (formData: FormData, data: any, parentKey = "") => {
  if (data && typeof data === "object" && !(data instanceof Date)) {
    Object.entries(data).forEach(([key, value]) => {
      const fullKey = parentKey ? `${parentKey}[${key}]` : key;

      if (value instanceof File) {
        formData.append(fullKey, value);
      } else if (Array.isArray(value)) {
        const hasFiles = value.some(v => v instanceof File);
        if (hasFiles) {
          value.forEach(file => {
            if (file instanceof File) {
              formData.append(`${fullKey}[]`, file);
            }
          });
        } else {
          value.forEach((item, i) => appendFilesFromData(formData, item, `${fullKey}[${i}]`));
        }
      } else if (value instanceof Date) {
        // Convert Date to ISO string
        formData.append(fullKey, value.toISOString());
      } else if (typeof value === "object" && value !== null) {
        appendFilesFromData(formData, value, fullKey);
      }
    });
  }
};

const stripFilesFromData = (data: any): any => {
  if (data instanceof File) return undefined; // skip files completely
  if (data instanceof Date) return data.toISOString(); // convert Date to string

  if (Array.isArray(data)) {
    const arr = data
      .map(stripFilesFromData)
      .filter(v => v !== undefined && !(Array.isArray(v) && v.length === 0) && !(typeof v === "object" && Object.keys(v).length === 0));
    return arr.length > 0 ? arr : undefined;
  }

  if (typeof data === "object" && data !== null) {
    const obj: Record<string, any> = {};
    Object.entries(data).forEach(([k, v]) => {
      const stripped = stripFilesFromData(v);
      if (stripped !== undefined) obj[k] = stripped;
    });
    return Object.keys(obj).length > 0 ? obj : undefined;
  }

  return data;
};

export const createMultipartFormData = (data: any): FormData => {
  const formData = new FormData();

  // Remove file objects before stringifying JSON
  const cleanData = stripFilesFromData(data);
  formData.append("data", JSON.stringify(cleanData));

  // Attach files separately (preserving structure)
  appendFilesFromData(formData, data);

  return formData;
};
